import React, { useCallback } from 'react'
import Button from 'components/ui/button'
import Tooltip from 'components/ui/tooltip'
import type { IconKey } from 'components/ui/icon'
type Props = {
  caption: string
  disabled?: boolean
  icon: IconKey
  selected: boolean
  value: any
  onClick: (value: any) => void
  onKeyDown?: (event: React.KeyboardEvent<HTMLElement>) => void
}
/**
 * A toggle button component that can be selected/deselected with icon and tooltip
 * @param caption - The tooltip text displayed on hover
 * @param disabled - Whether the button is disabled
 * @param icon - The icon to display in the button
 * @param selected - Whether the button is currently selected
 * @param value - The value associated with this button
 * @param onClick - Callback called when the button is clicked
 * @param onKeyDown - Optional callback for keyboard events
 * @returns A toggle button with icon and tooltip
 */
const ToggleButton = React.forwardRef<typeof Button, Props>(
  (
    { caption, disabled, icon, selected, value, onClick, onKeyDown },
    ref: any,
  ) => {
    const handleClick = useCallback(
      (event: React.SyntheticEvent<any>) => {
        event.preventDefault()
        onClick(value)
      },
      [value, onClick],
    )
    return (
      <Tooltip content={caption}>
        <Button
          compact
          appearance={selected ? 'primary' : 'icon'}
          disabled={disabled}
          icon={icon}
          ref={ref}
          onClick={handleClick}
          onKeyDown={onKeyDown}
        />
      </Tooltip>
    )
  },
)
export default React.memo(ToggleButton)
